import { useCallback, useEffect, useState } from "react";

export const COOKIE_CONSENT_STORAGE_KEY = "cookie-consent-v1";

export type CookieConsentCategories = {
  essential: true;
  analytics: boolean;
};

export type CookieConsentRecord = CookieConsentCategories & {
  ts: number;
};

const DEFAULT_CONSENT: CookieConsentCategories = {
  essential: true,
  analytics: false,
};

function readStoredConsent(): CookieConsentRecord | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CookieConsentRecord;
    if (typeof parsed !== "object" || parsed === null) return null;
    if (typeof parsed.ts !== "number") return null;
    return {
      essential: true,
      analytics: Boolean(parsed.analytics),
      ts: parsed.ts,
    };
  } catch {
    return null;
  }
}

function persistConsent(
  categories: CookieConsentCategories
): CookieConsentRecord {
  const record: CookieConsentRecord = {
    essential: true,
    analytics: categories.analytics,
    ts: Date.now(),
  };
  window.localStorage.setItem(
    COOKIE_CONSENT_STORAGE_KEY,
    JSON.stringify(record)
  );
  window.dispatchEvent(
    new CustomEvent("cookie-consent-change", { detail: record })
  );
  return record;
}

/**
 * Hook to read and update the visitor's cookie consent.
 * Mirrors the hotsite hook so the localStorage shape is identical.
 */
export function useCookieConsent() {
  const [consent, setConsentState] = useState<CookieConsentRecord | null>(null);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    setConsentState(readStoredConsent());
    setHydrated(true);

    function handleChange(event: Event) {
      const detail = (event as CustomEvent<CookieConsentRecord | null>).detail;
      setConsentState(detail ?? readStoredConsent());
    }

    window.addEventListener("cookie-consent-change", handleChange);
    return () => {
      window.removeEventListener("cookie-consent-change", handleChange);
    };
  }, []);

  const setConsent = useCallback((categories: CookieConsentCategories) => {
    const record = persistConsent(categories);
    setConsentState(record);
    return record;
  }, []);

  const acceptAll = useCallback(() => {
    return persistConsent({ essential: true, analytics: true });
  }, []);

  const acceptEssentialOnly = useCallback(() => {
    return persistConsent({ ...DEFAULT_CONSENT });
  }, []);

  const reopen = useCallback(() => {
    if (typeof window === "undefined") return;
    window.localStorage.removeItem(COOKIE_CONSENT_STORAGE_KEY);
    setConsentState(null);
    window.dispatchEvent(
      new CustomEvent("cookie-consent-change", { detail: null })
    );
  }, []);

  return {
    consent,
    hydrated,
    hasDecided: consent !== null,
    analyticsAllowed: consent?.analytics ?? false,
    setConsent,
    acceptAll,
    acceptEssentialOnly,
    reopen,
    defaults: DEFAULT_CONSENT,
  };
}
